import React, { Component } from 'react';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Button, Header } from 'semantic-ui-react';
import LoaderComponent from 'components/LoaderComponent';
import styled from 'styled-components';
import { numberFormatter } from 'utils/fomattingUtils';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import moment from 'moment';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { FormApi } from 'final-form';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import {
  BillJournalData,
  BillJournalPrintRequest,
  BillJournalPrintResponse,
  BillJournalSearchResponse
} from 'ts-types/api.types';
import ErrorMessage from 'components/final-form/ErrorMessage';
import { BillReceiver, BillStatus } from 'ts-types/api.enums';
import { withRouterWorkaround } from 'utils/workaroundUtils';
import { RouteComponentProps } from 'react-router';
import { BillJournalFormData } from 'components/billing/bill-journal/BillJournalView';
import { getBillJournalPdfUrl, getBillJournalPrintPreview } from 'services/billJournalServices';
import PdfModal from 'components/pdf-modal/PdfModal';
import SearchResultComponent from 'components/SearchResultComponent';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__row.totalRow {
    font-weight: 700;
    background-color: rgba(0, 0, 0, .2);

    &:hover {
      background-color: rgba(0, 0, 0, .2) !important;
    }
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledTotalsRowTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    display: none;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerRow.headerRow {
    background-color: rgba(0, 0, 0, .2);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
    padding-right: 1rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: none;
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn.noBorder {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const BillsContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  position: relative;
  min-height: 220px;
`;

const TotalsContainer = styled.div`
  position: relative;
  min-height: 46px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-flow: column;
  height: 100%;

  .bill-list-form {
    display: flex;
    flex-direction: column;
  }
`;

const StyledFormContent = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-flow: row wrap;
  justify-content: space-around;
  margin-bottom: 15px;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;
  display: flex;
  flex-direction: column;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  &.margin {
    margin-left: 20px;
  }
`;

const StyledCell = styled.div`
  text-align: right;
  padding-right: 0.4rem;
`;

const StyledPatientId = styled.span`
  color: #757575;
`;

const StyledFormButtonsRow = styled(FormButtonsRow)`
  margin-top: 1rem;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  billJournalData: BillJournalData,
  billsLoading: boolean,
  formData: BillJournalFormData,
  setErrorMessage: (errorMessage?: string) => void
}

interface State {
  billJournalPdfData?: BillJournalPrintResponse,
  billJournalPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

class BillJournalTableSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      billJournalPdfModalOpen: false,
      cancelTokenSource: cancelTokenSource
    };
  }

  getPdfUrl = (billJournalResponse: BillJournalPrintResponse) => {
    return getBillJournalPdfUrl(billJournalResponse.pdfId, billJournalResponse.pdfName);
  };

  openBillJournalPdfModal = (billJournalResponse: BillJournalPrintResponse) => {
    this.setState({
      billJournalPdfModalOpen: true,
      billJournalPdfData: billJournalResponse
    })
  };

  closeBillJournalPdfModal = () => {

    this.setState({
      billJournalPdfModalOpen: false,
      billJournalPdfData: undefined
    });
  };

  billDateCellRenderer = ({rowData}: any) => {
    const {billJournalData: {bills}} = this.props;
    if (rowData.index > bills.length) {
      return "";
    }
    return (
        <div>{moment(rowData.date).format("DD.MM.YYYY")}</div>
    );
  }
  amountCellRenderer = ({rowData}: any) => {
    return <StyledCell>{numberFormatter.format(rowData.amount)}</StyledCell>
  };
  paidAmountCellRenderer = ({rowData}: any) => {
    return <StyledCell>{numberFormatter.format(rowData.paidAmount)}</StyledCell>
  };

  openAmountCellRenderer = ({rowData}: any) => {
    return <StyledCell>{numberFormatter.format(rowData.openAmount)}</StyledCell>
  };

  stornoAmountCellRenderer = ({rowData}: any) => {
    return <StyledCell>{numberFormatter.format(rowData.stornoAmount)}</StyledCell>
  };


  billsRowGetter = (bills: Array<BillJournalSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billReceiverCellRenderer = ({rowData, cellData}: any) => {
    const {billJournalData: {bills}} = this.props;
    if (rowData.index > bills.length) {
      return "";
    }
    if (cellData === BillReceiver.PATIENT) {
      return (
          <div>{`${rowData.patientFullName}, ${rowData.patientCity}`}</div>
      );
    }
    return (
        <div>{`${rowData.guarantorDescription}`}</div>
    );
  };

  patientCellRenderer = ({rowData, cellData}: any) => {
    const {billJournalData: {bills}, getMessage} = this.props;
    if (rowData.index > bills.length) {
      return "";
    }
    if (cellData) {
      return (
          <div><StyledPatientId>{rowData.patientId}</StyledPatientId> - {rowData.patientFullName}</div>
      );
    }

    return getMessage("billJournal.various");
  };

  statusAmountCellRenderer = ({rowData}: any) => {
    const {billJournalData: {bills}, getMessage} = this.props;

    if (rowData.index > bills.length) {
      return "";
    }

    if (BillStatus.BILL === rowData.status) {
      if (rowData.hasReminders) {
        return (<div>{getMessage("bill.status.reminded")}</div>);
      }
      return (<div>{getMessage("bill.status.open")}</div>);
    }

    if (BillStatus.PAID_OPEN === rowData.status) {
      if (rowData.hasReminders) {
        return (<div>{getMessage("bill.status.reminded")}</div>);
      }
      return (<div>{getMessage("bill.status.paidOpen")}</div>);
    }

    if (BillStatus.PAID_CLOSED === rowData.status) {
      return (<div>{getMessage("bill.status.paidClosed")}</div>);
    }

    if (BillStatus.STORNO === rowData.status) {
      return (<div>{getMessage("bill.status.storno")}</div>);
    }
  }

  billsRowRenderer = ({className, columns, index, key, style}: any) => {

    const {billJournalData: {bills}} = this.props;
    const numberOfBills = bills.length;

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    const totalIx = index + 1;

    if (totalIx > numberOfBills) {

      const noBorderFieldsIndexes = [0, 1, 2];

      className = className + " totalRow";

      return (
          <div
              {...a11yProps}
              className={className}
              key={key}
              role="row"
              style={styles}
          >
            {
              columns.map((column: any, index: number) => {
                let editColumn = {...column, props: {...column.props}};
                if (noBorderFieldsIndexes.includes(index)) {
                  editColumn.props.className = `${editColumn.props.className}noBorder`;
                  return editColumn;
                }
                return editColumn;
              })
            }
          </div>
      );
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
        >
          {columns}
        </div>
    );
  };

  handleSubmit = async (values: Partial<BillJournalFormData>) => {

    const {bills} = values;
    const {cancelTokenSource} = this.state;
    const {setErrorMessage} = this.props;

    if (bills && bills.length) {
      const billIds: Array<number> = bills.map(bill => bill.id);

      const billJournalPrintRequest: Partial<BillJournalPrintRequest> = {
        mandateId: values.mandateId,
        fromDate: values.fromDate,
        toDate: values.toDate,
        orderBy: values.orderBy,
        billIds: billIds
      }

      await getBillJournalPrintPreview(billJournalPrintRequest, cancelTokenSource)
          .then(response => {
            this.openBillJournalPdfModal(response);

          })
          .catch((error) => setErrorMessage("error.general"));
    }
  };

  render(): React.ReactNode {
    const {billJournalPdfData, billJournalPdfModalOpen} = this.state;
    const {formData} = this.props;

    return (
        <React.Fragment>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
          {
            billJournalPdfModalOpen &&
            <PdfModal
                modalOpen={billJournalPdfModalOpen}
                onCloseModal={this.closeBillJournalPdfModal}
                pdfResponses={[billJournalPdfData]}
                getPdfUrl={this.getPdfUrl}
            />
          }
        </React.Fragment>
    )
  };

  renderForm = ({handleSubmit, submitting, values, form}: FormRenderProps): React.ReactNode => {
    const {bills} = values;
    const {billsLoading, getMessage} = this.props;

    const countBills = bills.length;
    const emptyBillArray = countBills < 1;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent className="bill-list-form">
            <Field
                name="error"
                component={ErrorMessage}
            />
            <StyledFormContent>
              <StyledContainer style={{width: "100%"}}>
                <Header>
                  <SearchResultComponent numResults={countBills} textMessage={"header.navigation.billJournalResults"}/>
                </Header>
                <BillsContainer>
                  {billsLoading
                      ? <LoaderComponent
                          message={`${getMessage("loading")} ${getMessage("bills").toLowerCase()}`}/>
                      : this.renderBillsTable(form, values, bills)}
                </BillsContainer>
                {
                  !emptyBillArray &&
                  <React.Fragment>
                    <TotalsContainer>
                      {this.renderTotalsRowTable()}
                    </TotalsContainer>
                  </React.Fragment>
                }
              </StyledContainer>
            </StyledFormContent>
          </FormContent>
          <StyledFormButtonsRow>
            <Button compact type="submit" primary disabled={submitting || emptyBillArray}>
              {getMessage("output")}
            </Button>
          </StyledFormButtonsRow>
        </StyledForm>
    );
  };

  renderBillsTable = (form: FormApi, values: any, bills: Array<BillJournalSearchResponse>): React.ReactNode => {

    const {getMessage} = this.props;

    const rowCount = bills.length;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer}
            rowCount={bills.length > 0 ? rowCount : 0}
            columns={[
              {
                width: 100,
                label: getMessage("billJournal.billNumber"),
                dataKey: "id"
              },
              {
                width: 500,
                label: getMessage("billJournal.billReceiver"),
                dataKey: "billReceiver",
                cellRenderer: this.billReceiverCellRenderer
              },
              {
                width: 500,
                label: getMessage("patient"),
                dataKey: "patientId",
                cellRenderer: this.patientCellRenderer
              },
              {
                width: 200,
                label: getMessage("date"),
                dataKey: "date",
                cellRenderer: this.billDateCellRenderer
              },
              {
                width: 150,
                label: getMessage("bill.amountHeader"),
                dataKey: "amount",
                cellRenderer: (data: any) => this.amountCellRenderer(data)
              },
              {
                width: 150,
                label: getMessage("billJournal.paid"),
                dataKey: "paidAmount",
                cellRenderer: (data: any) => this.paidAmountCellRenderer(data)
              },
              {
                width: 150,
                label: getMessage("bill.status.open"),
                dataKey: "amount",
                cellRenderer: (data: any) => this.openAmountCellRenderer(data)
              },
              {
                width: 150,
                label: getMessage("billJournal.stornoed"),
                dataKey: "stornoedAmount",
                cellRenderer: (data: any) => this.stornoAmountCellRenderer(data)
              },
              {
                width: 200,
                label: getMessage("status"),
                dataKey: "status",
                cellRenderer: this.statusAmountCellRenderer
              }
            ]}
        />
    )
  };

  headerRowRenderer = ({className, columns, style}: any) => {
    return (
        <StyledCell
            className={className}
            role="row"
            style={style}
        >
          {columns}
        </StyledCell>
    )
  };

  renderTotalsRowTable = (): React.ReactNode => {

    const {billJournalData} = this.props;

    return (
        <StyledTotalsRowTable
            headerRowRenderer={this.headerRowRenderer}
            rowGetter={this.billsRowGetter([])}
            rowRenderer={this.billsRowRenderer}
            rowCount={0}
            columns={[
              {
                width: 100,
                label: "",
                dataKey: "id"
              },
              {
                width: 500,
                label: "",
                dataKey: "billReceiver"
              },
              {
                width: 500,
                label: "",
                dataKey: "patientId"
              },
              {
                width: 210,
                label: "",
                dataKey: "date"
              },
              {
                width: 150,
                label: numberFormatter.format(billJournalData.totalAmount),
                dataKey: "amount"
              },
              {
                width: 150,
                label: numberFormatter.format(billJournalData.totalPaidAmount),
                dataKey: "paidAmount"
              },
              {
                width: 150,
                label: numberFormatter.format(billJournalData.totalOpenAmount),
                dataKey: "openAmount"
              },
              {
                width: 150,
                label: numberFormatter.format(billJournalData.totalStornoAmount),
                dataKey: "stornoedAmount"
              },
              {
                width: 200,
                label: "",
                dataKey: "status"
              }
            ]}
        />
    )
  };
}

export default withAuthContext(withRouterWorkaround(withMessages(BillJournalTableSegment)));
